import type { FileMeta } from "@rhema/client";
import { currentPlatform } from "../platform";

// Works out what a file is before it is encrypted: its kind, size on screen, length and a tiny
// blurred preview. Everything here runs on this device. Each step gives up quietly if the browser
// cannot do it, and the file is still sent.

export type MediaKind = "photo" | "video" | "file" | "voice";

export const WAVEFORM_BARS = 40;
const THUMB_SIZE = 32;

export function kindOf(file: Blob): MediaKind {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function formatDuration(seconds: number | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** File extension in capitals, for the badge on a file card. */
export function extensionOf(name: string | undefined, mime: string): string {
  const ext = name?.includes(".") ? name.split(".").pop() : mime.split("/").pop();
  return (ext ?? "file").slice(0, 4).toUpperCase();
}

/** Peak level of each slice of the recording, scaled so the loudest bar is 100. */
export function peaksOf(samples: ArrayLike<number>, bars = WAVEFORM_BARS): number[] {
  const out: number[] = [];
  const size = Math.max(1, Math.floor(samples.length / bars));
  for (let b = 0; b < bars; b++) {
    let peak = 0;
    const end = Math.min(samples.length, (b + 1) * size);
    for (let i = b * size; i < end; i++) peak = Math.max(peak, Math.abs(samples[i] ?? 0));
    out.push(peak);
  }
  const loudest = Math.max(...out);
  return out.map((p) => (loudest > 0 ? Math.max(4, Math.round((p / loudest) * 100)) : 4));
}

function withTimeout<T>(job: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    job,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

function canDraw(): boolean {
  return typeof createImageBitmap === "function" && typeof document !== "undefined";
}

function thumbnailOf(source: CanvasImageSource, width: number, height: number): string | undefined {
  const scale = THUMB_SIZE / Math.max(width, height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return undefined;
  ctx.filter = "blur(1.5px)";
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.6);
}

async function imageMeta(file: Blob): Promise<FileMeta> {
  const bitmap = await createImageBitmap(file);
  try {
    return {
      width: bitmap.width,
      height: bitmap.height,
      thumbnail: thumbnailOf(bitmap, bitmap.width, bitmap.height),
    };
  } finally {
    bitmap.close();
  }
}

function once(target: EventTarget, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    target.addEventListener(event, () => resolve(), { once: true });
    target.addEventListener("error", () => reject(new Error(event)), { once: true });
  });
}

async function videoMeta(file: Blob): Promise<FileMeta> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;
    await once(video, "loadeddata");
    const meta: FileMeta = {
      width: video.videoWidth,
      height: video.videoHeight,
      duration: Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : undefined,
    };
    video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
    await once(video, "seeked");
    meta.thumbnail = thumbnailOf(video, video.videoWidth, video.videoHeight);
    return meta;
  } finally {
    video.removeAttribute("src");
    URL.revokeObjectURL(url);
  }
}

/** Length and a 40 bar waveform of a recording. */
export async function audioMeta(file: Blob): Promise<FileMeta> {
  const Ctx =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return {};
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await file.arrayBuffer());
    return {
      duration: Math.round(audio.duration * 10) / 10,
      waveform: peaksOf(audio.getChannelData(0)),
    };
  } finally {
    void ctx.close();
  }
}

/** Everything known about a file, ready to go inside the encrypted message. */
export async function describeFile(file: Blob, kind: MediaKind): Promise<FileMeta> {
  const name = (file as File).name || undefined;
  const base: FileMeta = { mime: file.type || "application/octet-stream", ...(name ? { name } : {}) };
  try {
    if (kind === "photo" && canDraw()) return { ...base, ...(await withTimeout(imageMeta(file), 8000)) };
    if (kind === "video" && canDraw()) return { ...base, ...(await withTimeout(videoMeta(file), 8000)) };
    if (kind === "voice") return { ...base, ...(await withTimeout(audioMeta(file), 8000)) };
  } catch {
    // Some formats, such as HEIC on most browsers, cannot be read here. Send without a preview.
  }
  return base;
}

/** Saves a decrypted file to the person's downloads, or where they choose in the desktop app. */
export function saveBlob(blob: Blob, name: string): void {
  const save = currentPlatform().saveFile;
  if (save) {
    void save(blob, name).catch((err) => console.error("Rhema could not save the file", err));
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
